import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, Send, MessageSquare, Calendar, CalendarCheck, Check, Paperclip, FileText, Edit2, AlertCircle, Trash2, Link2, CheckCircle, Copy, ChevronDown, ArrowRightLeft } from 'lucide-react';
import { tasksApi, attachmentsApi, projectsApi, Attachment } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { Button } from '@/components/ui/Button';
import { ConvertTaskToDiscussionModal } from '@/components/tasks/ConvertTaskToDiscussionModal';
import { AssigneePicker } from '@/components/tasks/pickers/AssigneePicker';
import { StatusPicker } from '@/components/tasks/pickers/StatusPicker';
import { PriorityPicker } from '@/components/tasks/pickers/PriorityPicker';
import { DueDatePicker } from '@/components/tasks/pickers/DueDatePicker';
import { StartDatePicker } from '@/components/tasks/pickers/StartDatePicker';
import { useTaskStatusConfigs } from '@/hooks/useTaskStatusConfigs';
import { toast } from '@/components/ui/Toaster';
import { Avatar } from '@/components/ui/Avatar';
import { AssigneeAvatar } from '@/components/tasks/AssigneeAvatar';
import { inactiveAccountLabel, isInactiveAccount } from '@/lib/account-status';
import { FilePreviewModal } from '@/components/ui/FilePreviewModal';
import { AttachmentThumbnail } from '@/components/ui/AttachmentThumbnail';
import { RichContent } from '@/components/ui/RichContent';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { useMentionableUsers } from '@/hooks/useMentionableUsers';
import { useDraft } from '@/hooks/useDraft';
import { cn, formatRelativeTime } from '@/lib/utils';
import { extractFilesFromDataTransfer } from '@/lib/fileSystemUtils';
import { IS_STANDALONE } from '@/config/standalone';

interface StagedFile {
  id: string;
  file: File;
  preview?: string;
}

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  /** Create mode: render the modal as a "new task" form (no task yet). */
  create?: boolean;
  defaultStatusId?: string;
  taskLists?: Array<{ id: string; name: string }>;
  onTaskCreated?: (taskId: string) => void;
  task?: {
    id: string;
    title: string;
    description?: string;
    status: string;
    statusConfig?: { id: string; name: string; color: string; isClosed: boolean } | null;
    priority: string;
    dueDate?: string;
    startDate?: string;
    assignee?: { id: string; name: string; avatarUrl?: string };
    assigneeId?: string;
    createdById?: string;
    createdBy?: { id: string; name: string; avatarUrl?: string };
    createdAt?: string;
    attachments?: Attachment[];
  };
  projectId: string;
  listId?: string;
  organizationId?: string;
  scrollToCommentId?: string;
}

// Comment Item Component
function CommentItem({
  comment,
  taskId,
  isAuthor,
  onUpdate,
  onDeleteAttachment,
  onPreview,
  isUpdating,
  onCopyLink,
  isCopied,
  mentionableUsers,
  isNew,
  onNewHighlightEnd,
  onUploadAttachments,
}: {
  comment: {
    id: string;
    content: string;
    createdAt: string;
    updatedAt?: string;
    author: { id: string; name: string; avatarUrl?: string };
    attachments?: Attachment[];
    _uploading?: boolean;
    _uploadingCount?: number;
  };
  taskId: string;
  isAuthor: boolean;
  onUpdate: (commentId: string, content: string) => void;
  onDeleteAttachment: (attachmentId: string) => void;
  onPreview: (attachment: Attachment) => void;
  isUpdating: boolean;
  onCopyLink: () => void;
  isCopied: boolean;
  mentionableUsers?: { id: string; name: string; avatarUrl?: string }[];
  isNew?: boolean;
  onNewHighlightEnd?: () => void;
  onUploadAttachments: (commentId: string, files: File[]) => Promise<void>;
}) {
  const elementRef = useRef<HTMLDivElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [editStagedFiles, setEditStagedFiles] = useState<StagedFile[]>([]);
  const [editUploading, setEditUploading] = useState(false);
  const [editDragging, setEditDragging] = useState(false);
  const attachments = comment.attachments || [];
  const isEdited = comment.updatedAt && comment.updatedAt !== comment.createdAt;

  useEffect(() => {
    if (isNew && elementRef.current) {
      elementRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const timer = setTimeout(() => onNewHighlightEnd?.(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isNew]);

  const handleSave = async () => {
    const hasTextChange = editContent && editContent !== '<p></p>' && editContent !== comment.content;
    const hasFiles = editStagedFiles.length > 0;

    if (hasTextChange) {
      onUpdate(comment.id, editContent);
    }

    if (hasFiles) {
      setEditUploading(true);
      try {
        await onUploadAttachments(comment.id, editStagedFiles.map(f => f.file));
        setEditStagedFiles([]);
      } catch (err) {
        console.error('Failed to upload attachments:', err);
        setEditUploading(false);
        return;
      }
      setEditUploading(false);
    }

    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditContent(comment.content);
    setEditStagedFiles([]);
    setIsEditing(false);
  };

  return (
    <div ref={elementRef} className={cn('flex gap-3 group', isNew && 'outline outline-2 outline-orange-400 rounded-xl')}>
      <Avatar
        src={comment.author.avatarUrl}
        name={comment.author.name}
        size="sm"
        className="shrink-0 ring-2 ring-white"
      />
      <div className="flex-1 min-w-0 bg-slate-50 rounded-xl px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-700 text-sm">
              {comment.author.name}
            </span>
            <span className="text-xs text-slate-400">
              {formatRelativeTime(comment.createdAt)}
            </span>
            {isEdited && (
              <span className="text-xs text-slate-300">edited</span>
            )}
            <button
              onClick={onCopyLink}
              className="p-1 hover:bg-slate-200 rounded transition-colors"
              title={isCopied ? 'Link copied!' : 'Copy link'}
            >
              {isCopied ? (
                <CheckCircle className="w-3 h-3 text-green-500" />
              ) : (
                <Link2 className="w-3 h-3 text-slate-400" />
              )}
            </button>
          </div>
          {isAuthor && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-slate-600 transition-opacity"
              title="Edit comment"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {isEditing ? (
          <div
            className={cn('mt-2 relative rounded-lg transition-colors', editDragging && 'ring-2 ring-accent ring-offset-2 bg-accent/5')}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setEditDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget as Node)) setEditDragging(false); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setEditDragging(false);
              const files = Array.from(e.dataTransfer.files);
              if (files.length > 0) {
                const newFiles = files.map((file) => ({
                  id: `staged-${Date.now()}-${Math.random()}`,
                  file,
                  preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
                }));
                setEditStagedFiles(prev => [...prev, ...newFiles]);
              }
            }}
          >
            {editDragging && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-accent/10 border-2 border-dashed border-accent pointer-events-none">
                <p className="text-sm font-medium text-accent">Drop files here</p>
              </div>
            )}
            <div className="max-h-[240px] overflow-y-auto">
              <RichTextEditor
                content={editContent}
                onChange={setEditContent}
                showToolbar={true}
                minHeight="60px"
                enableMentions={true}
                mentionableUsers={mentionableUsers}
              />
            </div>
            {editStagedFiles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {editStagedFiles.map((staged) => (
                  <div
                    key={staged.id}
                    className="flex items-center gap-2 px-2 py-1 bg-white rounded-lg border border-slate-200 text-xs"
                  >
                    {staged.preview ? (
                      <img src={staged.preview} alt="" className="h-5 w-5 object-cover rounded" />
                    ) : (
                      <FileText className="h-4 w-4 text-grey-400" />
                    )}
                    <span className="max-w-[100px] truncate text-grey-600">{staged.file.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setEditStagedFiles(prev => {
                          const f = prev.find(sf => sf.id === staged.id);
                          if (f?.preview) URL.revokeObjectURL(f.preview);
                          return prev.filter(sf => sf.id !== staged.id);
                        });
                      }}
                      className="text-grey-400 hover:text-red-500 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {editUploading && (
              <div className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin text-grey-400" />
                <span>Uploading files...</span>
              </div>
            )}
            <div className="flex items-center gap-2 mt-2">
              <input
                ref={editFileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) {
                    const newFiles = Array.from(e.target.files).map((file) => ({
                      id: `staged-${Date.now()}-${Math.random()}`,
                      file,
                      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
                    }));
                    setEditStagedFiles(prev => [...prev, ...newFiles]);
                    e.target.value = '';
                  }
                }}
              />
              <button
                type="button"
                onClick={() => editFileInputRef.current?.click()}
                className="p-1.5 text-grey-400 hover:text-grey-600 rounded-full hover:bg-grey-100 transition-colors"
                title="Attach files"
                disabled={isUpdating || editUploading}
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                disabled={isUpdating || editUploading}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={(!editContent || editContent === '<p></p>') && editStagedFiles.length === 0 || isUpdating || editUploading}
              >
                {isUpdating || editUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <RichContent html={comment.content} markdownFallback className="text-sm text-slate-600 mt-1" />
        )}
        {/* Uploading indicator */}
        {comment._uploading && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin text-grey-400" />
            <span>Uploading {comment._uploadingCount} file{comment._uploadingCount !== 1 ? 's' : ''}...</span>
          </div>
        )}
        {attachments.length > 0 && !comment._uploading && (
          <div className="mt-3 flex flex-wrap gap-2">
            {attachments.map((attachment) => {
              return (
                <div key={attachment.id} className="relative group/attachment">
                  <button
                    type="button"
                    onClick={() => onPreview(attachment)}
                    className="block text-left"
                  >
                    <AttachmentThumbnail
                      thumbnailUrl={attachment.thumbnailSmUrl}
                      originalUrl={attachment.url}
                      mimeType={attachment.mimeType}
                      fileName={attachment.fileName}
                      createdAt={attachment.createdAt}
                      className="max-w-[200px] max-h-[150px] rounded-lg border border-slate-200 overflow-hidden"
                      imgClassName="max-w-[200px] max-h-[150px] object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 rounded-b-lg">
                      <span className="text-white text-xs truncate block">{attachment.fileName}</span>
                    </div>
                  </button>
                  {isAuthor && (
                    <button
                      onClick={() => onDeleteAttachment(attachment.id)}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover/attachment:opacity-100 transition-opacity hover:bg-red-600 shadow-sm"
                      title="Remove attachment"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** Labeled field on the dark agency panel: white label + a light chip holding a picker. */
function PanelField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-medium uppercase tracking-wider text-white/50 mb-1.5">{label}</label>
      <div className="rounded-lg bg-white px-2 py-1.5 flex items-center min-h-[2.25rem]">{children}</div>
    </div>
  );
}

export function TaskDetailModal({ isOpen, onClose, onEdit, onDuplicate, create, defaultStatusId, taskLists, onTaskCreated, task: taskProp, projectId, listId, organizationId, scrollToCommentId }: TaskDetailModalProps) {
  const isCreate = !!create;
  // In create mode there's no task yet — use a safe empty object so the many
  // `task.X` reads below don't crash; the create render branch uses draft state.
  const task: any = taskProp ?? {};
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const commentDraft = useDraft(`draft:task-comment:${task?.id ?? ''}`);
  const [newComment, setNewComment] = useState(() => commentDraft.read());
  /**
   * Phone-first comment composer, matching the discussion reply (MessageThread):
   * an open editor with a toolbar eats the screen on a phone, leaving no room to
   * read the task you came to read. On small screens it collapses to a single
   * tap target and opens on demand; a restored draft opens it so nobody loses
   * sight of unsent words. Desktop is unchanged.
   */
  const [commentOpen, setCommentOpen] = useState(() => {
    if (typeof window === 'undefined' || window.innerWidth >= 640) return true;
    const draft = commentDraft.read();
    return !!draft && draft !== '<p></p>';
  });
  const [commentKey, setCommentKey] = useState(0);
  const { data: mentionableUsers = [], isLoading: mentionUsersLoading } = useMentionableUsers(projectId);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [copiedCommentId, setCopiedCommentId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [newCommentId, setNewCommentId] = useState<string | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Copy task link to clipboard
  const handleCopyLink = async () => {
    const taskUrl = `${window.location.origin}/projects/${projectId}/tasks/${listId}?taskId=${task.id}`;
    try {
      await navigator.clipboard.writeText(taskUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCopyCommentLink = async (commentId: string) => {
    const url = `${window.location.origin}/projects/${projectId}/tasks/${listId}?taskId=${task.id}&commentId=${commentId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCommentId(commentId);
      setTimeout(() => setCopiedCommentId(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Admin / staff of THIS project: Solid anywhere, a partner agency on its own
  const { isProjectAdmin: isAgencyAdmin, isProjectStaff: isAgencyMember } = useProjectRoles(projectId);

  const isAssignee = task.assigneeId === user?.id || task.assignee?.id === user?.id;

  // Permission: can toggle completion
  // - Agency members: can toggle any task
  // - Clients: can only toggle tasks assigned to them
  const canToggleTask = isAgencyMember || isAssignee;

  // Permission: can edit/delete
  // - Agency members: can edit any task
  // - Clients: can only edit tasks they created
  const canEditTask = isAgencyMember || task.createdById === user?.id;
  const canDeleteTask = canEditTask;
  const navigate = useNavigate();
  const [showConvert, setShowConvert] = useState(false);
  // "Project admin" = agency staff, or a member whose project role is admin.
  const { data: projectForRole } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: isOpen && !isCreate && !!projectId,
  });
  const isProjectAdmin =
    isAgencyMember ||
    ((projectForRole?.members ?? []) as Array<{ userId: string; role?: string }>).some(
      (m) => m.userId === user?.id && m.role === 'admin',
    );
  const canConvert = isProjectAdmin && !!task?.id;
  // Agency editing sidebar — agency-only and additive (clients are unaffected).
  const showAgencyPanel = isAgencyMember;
  const { data: statusConfigs } = useTaskStatusConfigs();
  const patchTask = useMutation({
    mutationFn: (data: any) => tasksApi.updateTask(task.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', task.id] });
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
    },
    onError: (e: any) => toast({ type: 'error', title: e.message || 'Update failed' }),
  });
  
  // Toggle task completion mutation
  const toggleTaskMutation = useMutation({
    mutationFn: () => tasksApi.toggleComplete(task.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['task', task.id] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['taskBoard', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      // Close the modal when completing a task (not when un-completing)
      if (!isCompleted) {
        onClose();
      }
    },
  });
  
  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: () => tasksApi.deleteTask(task.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['taskBoard', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
    onError: (error) => {
      console.error('Failed to delete task:', error);
      setShowDeleteConfirm(false);
    },
  });
  
  // Fetch full task data (includes attachments with presigned URLs)
  const { data: fullTask } = useQuery({
    queryKey: ['task', task.id],
    queryFn: () => tasksApi.getTask(task.id),
    enabled: isOpen && !isCreate,
  });

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId),
    enabled: isOpen,
  });

  const orgName = user?.organizations?.find((o: any) => o.id === organizationId)?.name;

  // Freshest task data for display (inline edits invalidate ['task', id]).
  const displayTask = fullTask ?? task;
  // Who may edit title/description: agency (any level) always; clients only
  // their own task, and only while it's still unassigned (locked once assigned).
  const canEditContent = showAgencyPanel || (task.createdById === user?.id && !displayTask.assigneeId);
  // Inline title / description editing (replaces the separate edit modal).
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const saveTitle = () => {
    const v = titleDraft.trim();
    setEditingTitle(false);
    if (v && v !== displayTask.title) patchTask.mutate({ title: v });
  };
  const saveDescription = () => {
    setEditingDescription(false);
    if (descriptionDraft !== (displayTask.description || '')) patchTask.mutate({ description: descriptionDraft });
  };

  // ---- Create mode (draft state for a task that doesn't exist yet) ----
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftListId, setDraftListId] = useState('');
  const [draftStatusId, setDraftStatusId] = useState<string | undefined>(undefined);
  const [draftPriority, setDraftPriority] = useState('MEDIUM');
  const [draftAssigneeIds, setDraftAssigneeIds] = useState<string[]>([]);
  const [draftDueDate, setDraftDueDate] = useState<string | null>(null);
  const [draftStartDate, setDraftStartDate] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && isCreate) {
      setDraftTitle('');
      setDraftDescription('');
      setDraftListId(listId ?? '');
      setDraftStatusId(defaultStatusId);
      setDraftPriority('MEDIUM');
      setDraftAssigneeIds([]);
      setDraftDueDate(null);
      setDraftStartDate(null);
    }
  }, [isOpen, isCreate, listId, defaultStatusId]);

  const createTaskMutation = useMutation({
    mutationFn: (payload: { listId: string; data: any }) => tasksApi.createTask(projectId, payload.listId, payload.data),
    onSuccess: (createdTask: any) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
      onTaskCreated?.(createdTask?.id);
      onClose();
    },
    onError: (e: any) => toast({ type: 'error', title: e.message || 'Failed to create task' }),
  });

  const handleCreate = () => {
    const title = draftTitle.trim();
    if (!title) return toast({ type: 'error', title: 'Enter a task title' });
    const targetList = listId || draftListId;
    if (!targetList) return toast({ type: 'error', title: 'Pick a list' });
    createTaskMutation.mutate({
      listId: targetList,
      data: {
        title,
        description: draftDescription || undefined,
        // Agency-only fields are only sent when the agency panel is available.
        ...(showAgencyPanel
          ? {
              statusId: draftStatusId,
              priority: draftPriority,
              assigneeIds: draftAssigneeIds,
              dueDate: draftDueDate ?? undefined,
              startDate: draftStartDate ?? undefined,
            }
          : {}),
      },
    });
  };

  const { data: fetchedComments, isLoading: commentsLoading } = useQuery({
    queryKey: ['task-comments', task.id],
    queryFn: () => tasksApi.getComments(task.id),
    enabled: isOpen && !isCreate,
  });

  // Local state for comments - synced with server data
  const [localComments, setLocalComments] = useState<any[]>([]);

  // Sync local comments with fetched data
  useEffect(() => {
    if (fetchedComments) {
      setLocalComments(fetchedComments);
    }
  }, [fetchedComments]);

  // Reset local comments when task changes or modal closes
  useEffect(() => {
    if (!isOpen) {
      setLocalComments([]);
    }
  }, [isOpen, task.id]);

  // Use local comments for rendering
  const comments = localComments;

  // Listen for real-time task updates (assignee changes, status changes, new comments)
  useEffect(() => {
    const handleTaskUpdated = (e: CustomEvent) => {
      if (e.detail.taskId === task.id) {
        queryClient.invalidateQueries({ queryKey: ['task', task.id] });
        queryClient.invalidateQueries({ queryKey: ['task-comments', task.id] });
      }
    };

    if (isOpen) {
      window.addEventListener('task-updated', handleTaskUpdated as EventListener);
      return () => window.removeEventListener('task-updated', handleTaskUpdated as EventListener);
    }
  }, [isOpen, task.id, queryClient]);

  // Scroll to target comment after comments load
  useEffect(() => {
    if (!scrollToCommentId || commentsLoading || comments.length === 0) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`comment-${scrollToCommentId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [scrollToCommentId, commentsLoading, comments]);

  const addCommentMutation = useMutation({
    mutationFn: (content: string) => tasksApi.addComment(task.id, content),
    // Don't invalidate here - we do it manually after file uploads complete
  });

  const updateCommentMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      tasksApi.updateComment(task.id, commentId, content),
    onSuccess: async () => {
      // Refresh comments to show updated content
      const refreshedComments = await tasksApi.getComments(task.id);
      setLocalComments(refreshedComments);
    },
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => attachmentsApi.delete(attachmentId),
    onSuccess: async () => {
      // Refresh comments to show updated attachment list
      const refreshedComments = await tasksApi.getComments(task.id);
      setLocalComments(refreshedComments);
    },
  });

  const handleUpdateComment = (commentId: string, content: string) => {
    updateCommentMutation.mutate({ commentId, content });
  };

  const handleDeleteAttachment = (attachmentId: string) => {
    deleteAttachmentMutation.mutate(attachmentId);
  };

  const handleUploadCommentAttachments = async (commentId: string, files: File[]) => {
    for (const file of files) {
      await attachmentsApi.uploadFile(file, organizationId!, 'taskComment', commentId);
    }
    const refreshedComments = await tasksApi.getComments(task.id);
    setLocalComments(refreshedComments);
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    const isCommentEmpty = !newComment || newComment === '<p></p>';
    if ((isCommentEmpty && stagedFiles.length === 0) || addCommentMutation.isPending || uploading) return;

    const filesToUpload = [...stagedFiles];
    const hasFiles = filesToUpload.length > 0;

    try {
      const commentContent = (!newComment || newComment === '<p></p>') ? 'Attached files' : newComment;
      const comment = await addCommentMutation.mutateAsync(commentContent);

      // Add the comment to local state immediately so it shows in the UI
      // Include uploading flag if there are files to upload
      if (comment) {
        setLocalComments(prev => [...prev, {
          ...comment,
          _uploading: hasFiles,
          _uploadingCount: filesToUpload.length
        }]);
        setNewCommentId(comment.id);
      }

      // Clear input immediately so user sees feedback
      setNewComment('');
      commentDraft.clear();
      setCommentKey(k => k + 1);
      setStagedFiles([]);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (hasFiles && comment?.id) {
        setUploading(true);
        for (const staged of filesToUpload) {
          await attachmentsApi.uploadFile(staged.file, organizationId!, 'taskComment', comment.id);
        }
        setUploading(false);

        // Refetch to get the comment with attachments
        const refreshedComments = await tasksApi.getComments(task.id);
        setLocalComments(refreshedComments);
      }

      // Invalidate task list queries so comment counts update
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['task', task.id] });
      queryClient.invalidateQueries({ queryKey: ['task-comments', task.id] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    } catch (error) {
      console.error('Failed to post comment:', error);
      setUploading(false);
      setStagedFiles([]);
    }
  };

  const handleFilesSelected = (files: FileList | File[]) => {
    const newFiles = Array.from(files).map((file) => ({
      id: `staged-${Date.now()}-${Math.random()}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    }));
    setStagedFiles((prev) => [...prev, ...newFiles]);
  };

  const removeStagedFile = (id: string) => {
    setStagedFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length > 0) {
      e.preventDefault();
      handleFilesSelected(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = await extractFilesFromDataTransfer(e.dataTransfer);
    if (files.length > 0) {
      handleFilesSelected(files);
    }
  };

  const handleEditClick = () => {
    if (onEdit) {
      onEdit();
    }
  };
  
  if (!isOpen) return null;

  // ---- Create mode: render a "new task" form sharing the agency modal view ----
  if (isCreate) {
    return (
      <div
        className={cn(
          'fixed inset-0 z-50 flex items-center justify-center p-4',
          !IS_STANDALONE && 'top-[var(--solid-rail-height,56px)] lg:top-0 lg:left-[var(--solid-rail-width,52px)]',
        )}
      >
        <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
        <div className={cn(
          'relative w-full bg-slate-50 rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex',
          showAgencyPanel ? 'max-w-4xl' : 'max-w-lg',
        )}>
          {/* Left: title + description */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="px-6 pt-5 pb-4 bg-white border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">New task</h2>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-grey-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <input
                autoFocus
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreate(); } }}
                placeholder="Task title"
                className="w-full text-lg font-semibold text-slate-800 bg-white border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-accent"
              />
              {!listId && taskLists && taskLists.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-grey-500 mb-1">List</label>
                  <select
                    value={draftListId}
                    onChange={(e) => setDraftListId(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-secondary focus:border-accent focus:outline-none"
                  >
                    <option value="">Select a list…</option>
                    {taskLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-grey-500 mb-1">Description</label>
                <RichTextEditor content={draftDescription} onChange={setDraftDescription} placeholder="Add a description…" />
              </div>
            </div>
            <div className="border-t border-slate-200 px-6 py-3 bg-slate-100 flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-grey-500 hover:text-secondary">Cancel</button>
              <button
                onClick={handleCreate}
                disabled={!draftTitle.trim() || createTaskMutation.isPending}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-accent text-white hover:bg-accent/90 disabled:opacity-50 transition-colors"
              >
                Create task
              </button>
            </div>
          </div>

          {/* Agency sidebar (sets the draft) */}
          {showAgencyPanel && (
            <aside className="hidden sm:flex w-64 shrink-0 flex-col bg-slate-800 text-white overflow-y-auto">
              <div className="p-4 space-y-4">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-white/50">Task settings</h3>
                <PanelField label="Status">
                  <StatusPicker
                    statusConfig={(statusConfigs ?? []).find((s: any) => s.id === draftStatusId) ?? null}
                    statusConfigs={statusConfigs ?? []}
                    onChange={(statusId) => setDraftStatusId(statusId)}
                  />
                </PanelField>
                <PanelField label="Priority">
                  <PriorityPicker priority={draftPriority} onChange={(p) => setDraftPriority(p)} />
                </PanelField>
                <PanelField label="Resource">
                  <AssigneePicker
                    assignees={(project?.members ?? []).filter((m: any) => draftAssigneeIds.includes(m.user.id)).map((m: any) => m.user)}
                    projectMembers={project?.members ?? []}
                    onChangeMultiple={(ids) => setDraftAssigneeIds(ids)}
                  />
                </PanelField>
                <PanelField label="Due date">
                  <DueDatePicker dueDate={draftDueDate} onChange={(d) => setDraftDueDate(d)} />
                </PanelField>
                <PanelField label="Start date">
                  <StartDatePicker startDate={draftStartDate} onChange={(d) => setDraftStartDate(d)} />
                </PanelField>
              </div>
            </aside>
          )}
        </div>
      </div>
    );
  }

  const isCompleted = task.status === 'COMPLETED';
  // Compare dates in UTC to avoid timezone issues
  const isOverdue = task.dueDate && (() => {
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    const dueDateUTC = Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate());
    const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
    return dueDateUTC < todayUTC;
  })() && !isCompleted;
  
  const priorityConfig = {
    LOW: { label: 'Low', color: 'text-grey-500', bg: 'bg-grey-100' },
    MEDIUM: { label: 'Medium', color: 'text-blue-600', bg: 'bg-blue-50' },
    HIGH: { label: 'High', color: 'text-orange-600', bg: 'bg-orange-50', icon: AlertCircle },
    URGENT: { label: 'Urgent', color: 'text-red-600', bg: 'bg-red-50', icon: AlertCircle },
  };
  
  const priority = priorityConfig[task.priority as keyof typeof priorityConfig] || priorityConfig.MEDIUM;
  const dynamicStatusConfig = (fullTask as any)?.statusConfig ?? task.statusConfig;
  const status = dynamicStatusConfig
    ? { label: dynamicStatusConfig.name, hexColor: dynamicStatusConfig.color, bg: null, color: null }
    : { label: task.status, hexColor: null, bg: 'bg-grey-100', color: 'text-grey-500' };
  const PriorityIcon = priority.icon;
  
  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        !IS_STANDALONE && 'top-[var(--solid-rail-height,56px)] lg:top-0 lg:left-[var(--solid-rail-width,52px)]',
      )}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={cn(
        'relative w-full bg-slate-50 rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex',
        showAgencyPanel ? 'max-w-4xl' : 'max-w-2xl',
      )}>
        {/* Left column: task content */}
        <div className="flex-1 flex flex-col min-w-0">
        {/* Header with gradient accent */}
        <div className={cn(
          'relative px-6 pt-5 pb-4 bg-white border-b border-slate-100',
          isCompleted && 'bg-gradient-to-r from-emerald-50 to-white'
        )}>
          {/* Top row: badges + action buttons */}
          <div className="flex items-start gap-2 mb-3 -mt-2 -mr-3">
            <div className={cn('flex items-center gap-2 flex-wrap mt-2', showAgencyPanel && 'hidden')}>
              <span
                className={cn('inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full', !status.hexColor && status.bg, !status.hexColor && status.color)}
                style={status.hexColor ? { backgroundColor: `${status.hexColor}20`, color: status.hexColor } : undefined}
              >
                {isCompleted && <Check className="w-3 h-3" />}
                {status.label}
              </span>
              <span className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full',
                priority.bg, priority.color
              )}>
                {PriorityIcon && <PriorityIcon className="w-3 h-3" />}
                {priority.label}
              </span>
            </div>
            {/* Close & Edit buttons */}
            <div className="ml-auto flex items-center gap-1">
              {canConvert && (
                <button
                  onClick={() => setShowConvert(true)}
                  className="p-2 hover:bg-white/80 rounded-lg transition-colors"
                  title="Convert to discussion"
                  aria-label="Convert to discussion"
                >
                  <ArrowRightLeft className="w-4 h-4 text-grey-500" />
                </button>
              )}
              <button
                onClick={handleCopyLink}
                className="p-2 hover:bg-white/80 rounded-lg transition-colors"
                title={linkCopied ? 'Link copied!' : 'Copy link to task'}
              >
                {linkCopied ? (
                  <CheckCircle className="w-4 h-4 text-success" />
                ) : (
                  <Link2 className="w-4 h-4 text-grey-500" />
                )}
              </button>
              {onDuplicate && (
                <button
                  onClick={() => onDuplicate()}
                  className="p-2 hover:bg-white/80 rounded-lg transition-colors"
                  title="Duplicate task"
                >
                  <Copy className="w-4 h-4 text-grey-500" />
                </button>
              )}
              {onEdit && canEditTask && (
                <button
                  onClick={handleEditClick}
                  className="p-2 hover:bg-white/80 rounded-lg transition-colors"
                  title="Edit task"
                >
                  <Edit2 className="w-4 h-4 text-grey-500" />
                </button>
              )}
              {canDeleteTask && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                  title="Delete task"
                >
                  <Trash2 className="w-4 h-4 text-grey-400 group-hover:text-red-500" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/80 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-grey-400" />
              </button>
            </div>
          </div>

          {/* Title with toggle checkbox */}
          <div className="flex items-start gap-3">
            {canToggleTask && (
              <button
                onClick={() => toggleTaskMutation.mutate()}
                disabled={toggleTaskMutation.isPending}
                className={cn(
                  'flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all mt-0.5',
                  isCompleted 
                    ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600' 
                    : 'border-slate-300 hover:border-emerald-400 hover:bg-emerald-50',
                  toggleTaskMutation.isPending && 'opacity-50 cursor-wait'
                )}
                title={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
              >
                {isCompleted && <Check className="w-4 h-4" />}
              </button>
            )}
            {editingTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); saveTitle(); }
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
                onBlur={saveTitle}
                className="flex-1 text-xl font-semibold text-slate-800 bg-white border border-accent/50 rounded-lg px-2 py-1 focus:outline-none focus:border-accent"
              />
            ) : (
              <h2
                onClick={() => { if (canEditContent) { setTitleDraft(displayTask.title); setEditingTitle(true); } }}
                className={cn(
                  'flex-1 text-xl font-semibold text-slate-800 rounded-lg -mx-2 px-2 py-1 transition-colors',
                  isCompleted && 'line-through text-slate-400',
                  canEditContent && 'cursor-text hover:bg-slate-100',
                )}
                title={canEditContent ? 'Click to edit' : undefined}
              >
                {displayTask.title}
              </h2>
            )}
          </div>

          {/* Project & Organization */}
          {(project?.name || orgName) && (
            <div className="flex items-center gap-2 mt-2">
              {project?.name && (
                <span className="text-xs font-medium text-slate-500">{project.name}</span>
              )}
              {orgName && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20">
                  {orgName}
                </span>
              )}
            </div>
          )}

          {/* Provenance: this task was created from a discussion */}
          {(fullTask as any)?.sourceMessage && !(fullTask as any).sourceMessage.deletedAt && (
            <Link
              to={
                (fullTask as any).sourceMessage.discussionType
                  ? `/projects/${(fullTask as any).sourceMessage.projectId}/custom/${(fullTask as any).sourceMessage.discussionType}/${(fullTask as any).sourceMessage.id}`
                  : `/projects/${(fullTask as any).sourceMessage.projectId}/discussions/${(fullTask as any).sourceMessage.id}`
              }
              onClick={onClose}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Linked discussion: {(fullTask as any).sourceMessage.title || 'Untitled Discussion'}
            </Link>
          )}

          {/* Meta row: Created by (left), Due Date + Assigned to (right) */}
          <div className="flex items-center justify-between mt-3 text-sm">
            <div className="flex items-center gap-4">
              {task.createdBy && (
                <div className="flex items-center gap-2">
                  <span className="text-grey-400">Created by</span>
                  <Avatar src={task.createdBy.avatarUrl} name={task.createdBy.name} size="xs" />
                  <span className="text-grey-600">{task.createdBy.name}</span>
                </div>
              )}
              {!showAgencyPanel && isAgencyMember && task.startDate && (
                <div className="flex items-center gap-1.5 text-grey-500">
                  <CalendarCheck className="w-4 h-4" />
                  <span className="font-medium text-sm">
                    {new Date(task.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                  </span>
                  <span className="text-xs text-grey-400">start</span>
                </div>
              )}
              {!showAgencyPanel && task.dueDate && (
                <div className={cn(
                  'flex items-center gap-1.5',
                  isOverdue ? 'text-red-600' : 'text-grey-500'
                )}>
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">
                    {new Date(task.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                  </span>
                  {isOverdue && <span className="text-xs">(overdue)</span>}
                </div>
              )}
            </div>
            {!showAgencyPanel && task.assignee && (
              <div className="flex items-center gap-2">
                <span className="text-grey-400">Assigned to</span>
                <AssigneeAvatar person={task.assignee} />
                <span className="text-grey-600">{task.assignee.name}</span>
                {isInactiveAccount(task.assignee.accountStatus) && (
                  <span className="rounded bg-error/10 px-1.5 py-0.5 text-[10px] font-medium text-error">
                    {inactiveAccountLabel(task.assignee.accountStatus)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {/* Description (inline editable) */}
          {(displayTask.description || canEditContent) && (
            <div className="px-6 py-4 bg-white border-b border-slate-100">
              {editingDescription ? (
                <div>
                  <RichTextEditor content={descriptionDraft} onChange={setDescriptionDraft} placeholder="Add a description…" />
                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={() => setEditingDescription(false)} className="px-3 py-1.5 text-sm text-grey-500 hover:text-secondary">Cancel</button>
                    <button onClick={saveDescription} className="px-3 py-1.5 text-sm font-medium rounded-lg bg-accent text-white hover:bg-accent/90">Save</button>
                  </div>
                </div>
              ) : displayTask.description ? (
                <div
                  onClick={() => { if (canEditContent) { setDescriptionDraft(displayTask.description || ''); setEditingDescription(true); } }}
                  className={cn(canEditContent && 'cursor-text hover:bg-slate-50 rounded-lg -mx-2 px-2 py-1 transition-colors')}
                  title={canEditContent ? 'Click to edit' : undefined}
                >
                  <RichContent html={displayTask.description} className="text-sm text-slate-600 leading-relaxed" />
                </div>
              ) : (
                <button
                  onClick={() => { setDescriptionDraft(''); setEditingDescription(true); }}
                  className="text-sm text-grey-400 hover:text-secondary transition-colors"
                >
                  + Add a description
                </button>
              )}
            </div>
          )}

          {/* Task Attachments */}
          {fullTask?.attachments && fullTask.attachments.length > 0 && (
            <div className="px-6 py-4 bg-white border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                <Paperclip className="w-4 h-4 text-slate-400" />
                Attachments
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 rounded-full font-medium">
                  {fullTask.attachments.length}
                </span>
              </h3>
              <div className="flex flex-wrap gap-3">
                {fullTask.attachments.map((attachment: any) => (
                    <div key={attachment.id} className="relative group/attachment">
                      <button
                        type="button"
                        onClick={() => setPreviewAttachment(attachment)}
                        className="block w-20 h-20 rounded-lg overflow-hidden border border-slate-200 hover:border-slate-300 transition-colors"
                      >
                        <AttachmentThumbnail
                          thumbnailUrl={attachment.thumbnailSmUrl}
                          originalUrl={attachment.url}
                          mimeType={attachment.mimeType}
                          fileName={attachment.fileName}
                          createdAt={attachment.createdAt}
                          className="w-full h-full"
                          imgClassName="w-full h-full object-cover"
                        />
                      </button>
                    </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className="px-6 py-4 bg-white flex-1">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-slate-400" />
                Comments
                {comments && comments.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 rounded-full font-medium">
                    {comments.length}
                  </span>
                )}
              </h3>
            </div>
            
            {commentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />
              </div>
            ) : comments && comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} id={`comment-${comment.id}`}>
                    <CommentItem
                      comment={comment}
                      taskId={task.id}
                      isAuthor={comment.author.id === user?.id}
                      onUpdate={handleUpdateComment}
                      onDeleteAttachment={handleDeleteAttachment}
                      onPreview={setPreviewAttachment}
                      isUpdating={updateCommentMutation.isPending}
                      onCopyLink={() => handleCopyCommentLink(comment.id)}
                      isCopied={copiedCommentId === comment.id}
                      mentionableUsers={mentionableUsers}
                      isNew={comment.id === newCommentId}
                      onNewHighlightEnd={() => setNewCommentId(null)}
                      onUploadAttachments={handleUploadCommentAttachments}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-slate-50 rounded-xl">
                <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">
                  No comments yet
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/50 rounded-2xl">
            <div className="bg-white rounded-xl shadow-xl p-6 mx-6 max-w-sm w-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800">Delete Task</h3>
              </div>
              <p className="text-sm text-slate-600 mb-6">
                Are you sure you want to permanently delete this task? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <Button 
                  variant="ghost" 
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteTaskMutation.isPending}
                >
                  Cancel
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => deleteTaskMutation.mutate()}
                  isLoading={deleteTaskMutation.isPending}
                >
                  Delete Task
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* File Preview Modal */}
        <FilePreviewModal
          isOpen={!!previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          attachment={previewAttachment}
          attachments={fullTask?.attachments}
          currentIndex={previewAttachment && fullTask?.attachments ? fullTask.attachments.findIndex((a: any) => a.id === previewAttachment.id) : 0}
          onNavigate={(index) => fullTask?.attachments && setPreviewAttachment(fullTask.attachments[index])}
        />

        {/* Comment Input - sticky footer */}
        <div className="border-t border-slate-200 px-6 py-3 bg-slate-100">
          {/* Collapsed: one tap target, nothing else competing for the screen.
              Rendered only when closed so the editor keeps its state once opened. */}
          {!commentOpen && (
            <button
              type="button"
              onClick={() => setCommentOpen(true)}
              className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:bg-slate-50"
            >
              <Avatar src={user?.avatarUrl} name={user?.name || 'You'} size="sm" />
              <span className="text-sm text-grey-500">Write a comment…</span>
            </button>
          )}
          <form onSubmit={handleSubmitComment} className={cn('flex items-start gap-3', !commentOpen && 'hidden')}>
            <Avatar
              src={user?.avatarUrl}
              name={user?.name || 'You'}
              size="sm"
              className="shrink-0 hidden sm:flex mt-2"
            />
            <div className="flex-1 min-w-0">
              {/* Minimise: reading is why you came. Phone only — on desktop the
                  composer costs nothing to leave open. */}
              <div className="mb-2 flex justify-end sm:hidden">
                <button
                  type="button"
                  onClick={() => setCommentOpen(false)}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-grey-500 hover:bg-grey-100 hover:text-secondary"
                >
                  <ChevronDown className="h-4 w-4" />
                  Minimise
                </button>
              </div>
              {/* Input with integrated staged files */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onPaste={handlePaste}
                className={cn(
                  'bg-white border-2 rounded-2xl shadow-sm overflow-hidden transition-colors',
                  isDragging
                    ? 'border-primary border-dashed bg-primary/5'
                    : 'border-slate-200 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary'
                )}
              >
                <div className="max-h-[240px] overflow-y-auto">
                  <RichTextEditor
                    key={commentKey}
                    content={newComment}
                    onChange={(html) => { setNewComment(html); commentDraft.write(html); }}
                    placeholder={isDragging ? 'Drop files here...' : 'Write a comment...'}
                    enableMentions={true}
                    mentionableUsers={mentionableUsers}
                    mentionUsersLoading={mentionUsersLoading}
                    enableEveryoneMention={true}
                    disabled={addCommentMutation.isPending || uploading}
                    showToolbar={true}
                    compactToolbar
                    minHeight="60px"
                    enableGifPicker={true}
                  />
                </div>

                {/* Staged Files Preview - inside the input container */}
                {stagedFiles.length > 0 && (
                  <div className="px-3 py-2 border-t border-slate-100 bg-slate-50">
                    <div className="flex flex-wrap gap-2">
                      {stagedFiles.map((staged) => (
                        <div
                          key={staged.id}
                          className="flex items-center gap-2 px-2 py-1 bg-white rounded-lg border border-slate-200 text-xs"
                        >
                          {staged.preview ? (
                            <img src={staged.preview} alt="" className="h-5 w-5 object-cover rounded" />
                          ) : (
                            <FileText className="h-4 w-4 text-grey-400" />
                          )}
                          <span className="max-w-[100px] truncate text-grey-600">{staged.file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeStagedFile(staged.id)}
                            className="text-grey-400 hover:text-red-500 transition-colors"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center justify-end gap-1 px-2 py-1.5 border-t border-slate-100">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    // @ts-expect-error — webkitdirectory is not in React's type defs
                    webkitdirectory=""
                    onChange={(e) => e.target.files && handleFilesSelected(e.target.files)}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1.5 text-grey-400 hover:text-grey-600 rounded-full hover:bg-grey-100 transition-colors"
                    title="Attach files"
                    disabled={addCommentMutation.isPending || uploading}
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <button
                    type="submit"
                    disabled={((!newComment || newComment === '<p></p>') && stagedFiles.length === 0) || addCommentMutation.isPending || uploading}
                    className={cn(
                      'p-1.5 rounded-full transition-colors',
                      ((!newComment || newComment === '<p></p>') && stagedFiles.length === 0)
                        ? 'text-grey-300 cursor-not-allowed'
                        : 'text-primary hover:bg-primary/10'
                    )}
                  >
                    {addCommentMutation.isPending || uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
        </div>{/* end left column */}

        {/* Agency editing sidebar (agency-only) */}
        {showAgencyPanel && (
          <aside className="hidden sm:flex w-64 shrink-0 flex-col bg-slate-800 text-white overflow-y-auto">
            <div className="p-4 space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-white/50">Task settings</h3>

              <PanelField label="Status">
                <StatusPicker
                  statusConfig={(fullTask ?? task).statusConfig}
                  statusConfigs={statusConfigs ?? []}
                  onChange={(statusId) => patchTask.mutate({ statusId })}
                />
              </PanelField>

              <PanelField label="Priority">
                <PriorityPicker
                  priority={(fullTask ?? task).priority}
                  onChange={(priority) => patchTask.mutate({ priority })}
                />
              </PanelField>

              <PanelField label="Resource">
                <AssigneePicker
                  assignees={displayTask.assignees ?? (displayTask.assignee ? [displayTask.assignee] : [])}
                  projectMembers={project?.members ?? []}
                  onChangeMultiple={(ids) => patchTask.mutate({ assigneeIds: ids })}
                />
              </PanelField>

              <PanelField label="Due date">
                <DueDatePicker
                  dueDate={(fullTask ?? task).dueDate}
                  onChange={(dueDate) => patchTask.mutate({ dueDate })}
                />
              </PanelField>

              <PanelField label="Start date">
                <StartDatePicker
                  startDate={(fullTask ?? task).startDate}
                  onChange={(startDate) => patchTask.mutate({ startDate })}
                />
              </PanelField>

            </div>
          </aside>
        )}
      </div>

      {canConvert && showConvert && (
        <ConvertTaskToDiscussionModal
          isOpen
          task={(fullTask as any) ?? task}
          onClose={() => setShowConvert(false)}
          onConverted={(discussion, mode) => {
            setShowConvert(false);
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['task', task.id] });
            queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
            queryClient.invalidateQueries({ queryKey: ['discussions', projectId] });
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            if (mode === 'move') onClose();
            navigate(
              discussion.discussionType
                ? `/projects/${projectId}/custom/${discussion.discussionType}/${discussion.id}`
                : `/projects/${projectId}/discussions/${discussion.id}`,
            );
          }}
        />
      )}
    </div>
  );
}
